import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, Stack } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { formAPI } from 'api/form';
import { useAppBuilderContext } from 'app/app/editor/context';
import useBundleTranslation from 'i18n';
import useCustomSimplifiedForm from 'components/common/form/hooks/useCustomSimplifiedForm';
import { TemplateFormComponent } from 'components/common/form/layout/ReactHookFormController';
import { FormTabType } from 'components/common/form/tab';
import { useFormState } from 'react-hook-form';
import { IComponentNode, processSettingsDefaults, processSettingsVariables } from 'app/app/editor/index.functions';
import { FormDataAPIType, RawFormComponentType } from 'components/common/form';
import { instance } from 'api/api';
import { APIResponse } from 'tools/types';
import { FormComponentValue } from 'components/common/form/layout/control';
import { AxiosResponse } from 'axios';

export default function SettingsForm({ node }: { node: IComponentNode }) {
    const { t } = useBundleTranslation();

    const ctx = useAppBuilderContext();
    const [page, setPage, sendMessage] = [ctx.page, ctx.setPage, ctx.sendMessage];

    const componentUid = node.params.uid;
    const component = page?.components[node.params.uid];

    const { status, data } = useQuery<any, Error>({
        queryKey: ['app-builder-component-form-json', component?.component ?? componentUid],
        queryFn: () => {
            if (!component) return null;
            return formAPI.load('/data/app/components/' + component.component + '/form.json');
        },
    });

    const [initialFormState, setInitialFormState] = useState(processSettingsDefaults(component, data));
    // If selected Node changed => update form default state
    useEffect(() => {
        setInitialFormState(processSettingsDefaults(component, data));
    }, [componentUid, component, data]);
    useEffect(() => {
        simpleForm.form.hookFormReset(initialFormState);
    }, [initialFormState]);

    const simpleForm = useCustomSimplifiedForm(initialFormState);

    const formSettings: { tabs: FormTabType[] } = useMemo(() => {
        const tabs: Array<FormTabType> = [];
        if (data?.data?.settings) {
            return data.data.settings;
        }
        return { tabs: tabs };
    }, [data?.data?.settings]);

    const { dirtyFields } = useFormState({ control: simpleForm.form.hookFormControl });
    const isDirty = Object.entries(dirtyFields).length > 0;

    if (!component) {
        return null;
    }

    const cancelClick = () => {
        simpleForm.form.hookFormReset();
    };

    const applySettings = () => {
        const formData = simpleForm.form.hookFormGetValues();
        simpleForm.form.hookFormReset(formData);

        let newPage = structuredClone(page);
        if (data.data?.fields) {
            let ret = processSettingsVariables(data.data?.fields, formData);
            newPage.components[componentUid].settings = ret.settings;
            newPage.components[componentUid].variables = ret.variables;
        } else newPage.components[componentUid].settings = formData;

        setPage(newPage, false);
        sendMessage({ name: 'updateComponent', data: { uid: componentUid, updateType: 'settings' }, broadcast: false });
    };

    if (status === 'pending') {
        return <Box>{t('loading___')}</Box>;
    } else if (status === 'error') {
        return <Box>{t('error___')}</Box>;
    }

    return (
        <>
            <Stack spacing={1}>
                {formSettings.tabs.map((tab: FormTabType) =>
                    tab.components.map((c) => (
                        <FormComponent
                            key={`${tab.name}_${c.name}`}
                            componentUid={componentUid}
                            component={c}
                            form={simpleForm}
                        />
                    )),
                )}
            </Stack>
            {isDirty && (
                <Stack
                    spacing={1.5}
                    direction={'row'}
                    sx={{
                        pt: 0.5,
                        mt: 0.5,
                        justifyContent: 'flex-end',
                        position: 'sticky',
                        bottom: 0,
                        backgroundColor: 'background.default',
                        '&:after': {
                            content: '""',
                            height: '16px',
                            width: '100%',
                            position: 'absolute',
                            top: '100%',
                            backgroundColor: 'background.default',
                        },
                    }}
                >
                    <Button onClick={() => applySettings()}>{'Apply'}</Button>
                    <Button variant={'outlined'} color="neutral" onClick={() => cancelClick()}>
                        {'Cancel'}
                    </Button>
                </Stack>
            )}
        </>
    );
}

function FormComponent({
    form,
    component,
    componentUid,
}: {
    form: FormDataAPIType;
    component: RawFormComponentType;
    componentUid: string;
}) {
    const { t } = useBundleTranslation();

    const { status, data } = useQuery<Array<FormComponentValue>, Error>({
        queryKey: ['app-builder-component-data', component.component, component.asyncDataUrl],
        queryFn: async () => {
            if (!component.asyncDataUrl) {
                return [];
            }
            const response: AxiosResponse<APIResponse<Array<FormComponentValue>>> = await instance.get(
                component.asyncDataUrl,
            );
            return response.data.status == 'OK' ? response.data.data : [];
        },
    });
    const ctx = useAppBuilderContext();
    const [pageInternalName, pageSession, isTemplate] = [ctx.pageInternalName, ctx.pageSession, ctx.isTemplate];

    if (status != 'success') {
        return <Box>{t('loading___')}</Box>;
    }

    const urlAdd = [
        '?pageInternalName=',
        pageInternalName,
        '&pageSession=',
        pageSession,
        '&componentUid=',
        componentUid,
        '&componentSetting=',
        component.name,
        '&isTemplate=',
        isTemplate ? 'Y' : 'N',
    ].join('');

    if (['UploadImage', 'UploadFile'].includes(component.component)) {
        if (undefined === component?.props) component.props = {};
        component.props['ajaxApiUrl'] = '/data/app/upload-asset' + urlAdd;
        component.props['ajaxApiRemoveUrl'] = '/data/app/remove-asset' + urlAdd;

        component.props['onAjaxUploadResult'] = (response: any) => {
            if (response.data.status === 'OK') {
                form.form.hookFormSetValue(component.name, response.data.relative_path);
            }
        };
    }

    if (['PageVariables'].includes(component.component)) {
        component.props['uploadAssetUrl'] = '/data/app/upload-asset' + urlAdd;
    }

    return (
        <TemplateFormComponent
            childComponentsRender={(form: FormDataAPIType, component: RawFormComponentType) => {
                return <FormComponent form={form} component={component} componentUid={componentUid} />;
            }}
            elementProps={form}
            component={component.component}
            components={component?.components}
            componentValues={component.data ?? data}
            name={component.name}
            props={{
                label: component.label ?? component.name,
                ...(component?.props ?? {}),
            }}
            deps={component.deps ?? []}
            wrapper={true}
        />
    );
}
